import { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Breadcrumb, Form, Select, Spin } from "antd";
import { GetClientByIdApi } from "../../../api/request/clients/clients.js";
import Information from "./Information.jsx";
import Documents from "./Documents.jsx";
import Communication from "./Communication.jsx";
import Organizer from "./Organizer.jsx";
import Notes from "./Notes.jsx";
import Referral from "./Referral.jsx";
import Invoice from "./Invoice.jsx";
import LoadableButton from "../../../components/buttons/LoadableButton.jsx";
import ModalComponent from "../../../components/modals/ModalComponent.jsx";
import {
  ExtraInvoiceSendApi,
  GetInvoiceListApi,
} from "../../../api/request/taskManager/taskManager.js";
import toast from "react-hot-toast";
import { getSocket } from "../../../services/Socket.js";

export default function Client() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { state } = useLocation();
  const [form] = Form.useForm();
  const socket = getSocket();
  const [isLoading, setIsLoading] = useState(false);
  const [client, setClient] = useState(null);
  const [isExtaInvoiceOpen, setIsExtaInvoiceOpen] = useState(false);
  const [isExtraLoading, setIsExtraLoading] = useState(false);
  const [invoiceOptions, setInvoiceOptions] = useState([]);
  const [isLoadingInvoice, setIsLoadingInvoice] = useState(false);

  const tabs = [
    { id: "2", title: "Information" },
    { id: "3", title: "Documents" },
    { id: "4", title: "Communication" },
    { id: "5", title: "Organizer" },
    { id: "6", title: "Invoice" },
    { id: "8", title: "Notes" },
    { id: "9", title: "Referral" },
  ];

  const [activeTab, setActiveTab] = useState(state?.setTab ? tabs[1] : tabs[0]);

  const getClientDetails = async () => {
    if (!id) return;

    try {
      setIsLoading(true);
      let params = { id: id };
      const { data } = await GetClientByIdApi(params);
      if (data?.status) {
        setClient(data?.data);
      }
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getClientDetails();
  }, [id]);

  const changeTab = (tab) => {
    setActiveTab(tab);
  };

  const breadcrumbItems = [
    {
      title: (
        <p
          className="cursor-pointer font-semibold text-black"
          onClick={() => navigate("/clients")}
        >
          Clients
        </p>
      ),
    },
    ...(client?.name
      ? [{ title: <p className="capitalize">{client?.name}</p> }]
      : []),
    ...(activeTab?.title
      ? [{ title: <p className="capitalize">{activeTab?.title}</p> }]
      : []),
  ];

  const renderTab = () => {
    switch (activeTab?.id) {
      case "2":
        return <Information client={client} setClient={setClient} />;
      case "3":
        return <Documents id={id} tab={activeTab} />;
      case "4":
        return <Communication client={client} />;
      case "5":
        return <Organizer id={id} tab={activeTab} />;
      case "6":
        return <Invoice id={id} />;
      case "8":
        return <Notes client={client} />;
      case "9":
        return <Referral id={id} />;
      default:
        return (
          <div className="flex items-center justify-center h-full text-secondaryLight1">
            Select a tab
          </div>
        );
    }
  };

  const sendInvoiceHandler = async (values) => {
    try {
      setIsExtraLoading(true);
      let params = { user_id: id, ...values };
      const { data } = await ExtraInvoiceSendApi(params);
      if (data?.status) {
        toast.success(data?.message);
        getClientDetails();
        socket.emit("updateTask", JSON.stringify({ task: 1, userId: id }));
      }
      form.resetFields();
      setIsExtaInvoiceOpen(false);
    } catch (error) {
      toast.error(error?.response?.data?.message);
    } finally {
      setIsExtraLoading(false);
    }
  };

  const openInvoiceModal = async () => {
    try {
      setIsExtaInvoiceOpen(true);
      setIsLoadingInvoice(true);
      const { data } = await GetInvoiceListApi();
      if (data?.status) {
        setInvoiceOptions(
          data?.data?.map((item) => {
            return {
              value: item?.id,
              label: `${item?.name} - $${item?.amount}`,
            };
          }),
        );
      }
    } catch (error) {
      toast.error(error?.response?.data?.message);
    } finally {
      setIsLoadingInvoice(false);
    }
  };

  return (
    <div>
      {isLoading ? (
        <div className="flex flex-col items-center gap-2 mt-48">
          <Spin />
          <p className="text-secondaryLight1">Loading...</p>
        </div>
      ) : (
        <div>
          <div className="flex justify-between items-center gap-2">
            <Breadcrumb items={breadcrumbItems} />
            {activeTab?.id == "6" && (
              <div>
                <LoadableButton
                  className="bg-primary text-white text-base font-semibold px-6 py-2 rounded-lg mt-2"
                  // isLoading={isLoadingInvoice}
                  onClick={openInvoiceModal}
                  lable="Add External Invoice"
                  type="primary"
                />
              </div>
            )}
          </div>
          <div className="grid md:grid-cols-4 gap-4 mt-4">
            <div className="border-2 rounded-lg">
              <div className="border-b-2 p-3 bg-secondaryLight rounded-t-lg">
                <p className="font-semibold uppercase">List</p>
              </div>
              <div className="relative h-[calc(100vh-194px)] overflow-y-auto">
                {tabs?.map((tab, index) => (
                  <div
                    key={index}
                    onClick={() => changeTab(tab)}
                    className={`cursor-pointer my-3 py-2 font-medium px-3 duration-300 ${
                      activeTab?.id == tab?.id
                        ? "bg-secondaryLight text-primary font-semibold"
                        : ""
                    }`}
                  >
                    {tab?.title}
                  </div>
                ))}
              </div>
            </div>

            <div className="md:col-span-3 border-2 rounded-lg relative">
              {renderTab()}
            </div>
          </div>
        </div>
      )}

      <ModalComponent
        isOpen={isExtaInvoiceOpen}
        setIsOpen={setIsExtaInvoiceOpen}
        title="Add External Invoice"
      >
        {isLoadingInvoice ? (
          <div className="flex justify-center flex-col my-[10%]">
            <Spin size="large" />
            <p className="primary text-center mt-2">Loading...</p>
          </div>
        ) : (
          <Form
            autoComplete="off"
            form={form}
            // initialValues={initialValues}
            onFinish={sendInvoiceHandler}
          >
            <Form.Item
              name="invoice_temp_id"
              rules={[
                { required: true, message: "Please select invoice type!" },
              ]}
            >
              <Select
                placeholder="Select Invoice"
                options={invoiceOptions}
                size="large"
              />
            </Form.Item>
            <div className="flex justify-center ">
              <LoadableButton
                className="bg-primary text-white uppercase text-base font-semibold w-full py-2 rounded-lg mt-2"
                type="submit"
                lable="Send Invoice"
                loadingLable="Sending Invoice..."
                isLoading={isExtraLoading}
              />
            </div>
          </Form>
        )}
      </ModalComponent>
    </div>
  );
}
